import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useBuilding, useFetchBuildingZoneSetupReportQuery } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices';
import { useAppSelector } from 'store/store.hook';

dayjs.extend(customParseFormat);

interface useZoneSetupReportProps {
  buildingId: number;
  start: string;
  end: string;
}

export const useZoneSetupReport = ({ buildingId, start, end }: useZoneSetupReportProps) => {
  const { orgName: organization, orgSlug } = useAppSelector(selectCurrentUserInfo);
  const {
    data: { address = '', city = '', state = '', zip = '' },
    isFetching: loadingBuilding,
  } = useBuilding({ buildingId });

  const {
    data: { cleaners: _cleaners = [], supervisors: _supervisors = [], floors: _floors = [] } = {},
    isFetching,
    ...rest
  } = useFetchBuildingZoneSetupReportQuery({
    orgSlug,
    id: buildingId,
  });

  const building = { organization, address, city, state, zip };

  const serviceDate: [string, string] = [
    dayjs(new Date(start)).format('YYYY-MM-DD'),
    dayjs(new Date(end)).format('YYYY-MM-DD'),
  ];

  const cleaners = (_cleaners === null ? [] : _cleaners).map(({ name = '', email = '', phone = '' } = {}) => ({
    name,
    email,
    phone,
  }));

  const supervisors = (_supervisors === null ? [] : _supervisors).map(({ name = '', email = '', phone = '' } = {}) => ({
    name,
    email,
    phone,
  }));

  const floors = _floors.map(({ name = '', zones = [] }) => {
    return {
      name,
      zones: zones.map(({ code = '', name = '', category = '', interval = '', frequency = 0 }) => {
        return {
          code,
          name,
          category,
          interval,
          frequency,
        };
      }),
    };
  });

  return {
    data: { building, serviceDate, cleaners, supervisors, floors },
    ...rest,
    isLoading: loadingBuilding || isFetching,
    isFetching,
  };
};
